<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from '#app/composables/router';
import { useAiPanel } from '#layers/ai/public';
import { OrganizationWorkspaceLauncher } from '#layers/global-settings/public';
import { workspaceRuntime } from '#layers/runtime/public';
import { Spacebar } from '#layers/ui-workspace/public';
import { ErrorBoundary, useDeferredTrue, usePaneStore } from '#layers/ui/public';
import { showsWorkspaceShell } from '~/utils/routes';

// Nuxt's useRoute() updates after page settlement. The shell must observe Vue Router immediately
// so it does not render one extra settling frame after the route and active runtime agree.
const route = useRouter().currentRoute;
const paneStore = usePaneStore();
const workspaceRuntimeKey = computed(() =>
	workspaceRuntime.activeRuntime.value?.identity ?? 'workspace-runtime-pending',
);
const emptyWorkspaceShell = computed(() => showsWorkspaceShell({
	phase: workspaceRuntime.state.value.phase,
	routeWorkspace: route.value.params.workspace,
	runtimeSlug: workspaceRuntime.activeRuntime.value?.slug ?? null,
}));

// Preserve an already-mounted page during preparation, but never mount an outgoing route
// against its replacement's runtime while Vue Router is still settling.
const mountedRuntimeKey = ref<string>();

watch([workspaceRuntimeKey, emptyWorkspaceShell], ([key, empty]) => {
	if (!empty) mountedRuntimeKey.value = key;
}, { immediate: true, flush: 'sync' });

const renderWorkspaceContent = computed(() =>
	workspaceRuntime.activeRuntime.value !== null && mountedRuntimeKey.value === workspaceRuntimeKey.value,
);
const showWorkspaceSwitching = useDeferredTrue(emptyWorkspaceShell, 500);

useAiPanel();
</script>

<template>
	<div class="relative flex h-dvh w-screen items-stretch bg-dimmed">
		<aside
			class="
				relative flex flex-col items-center pb-safe inline-(--spacebar-width)
				after:absolute after:inset-y-0 after:inset-s-full after:border-e after:transition-colors
			"
			:class="{
				'after:border-transparent': paneStore.sidebarSplitPanelCollapsed,
			}"
		>
			<div class="flex shrink-0 items-center block-(--primary-header-height)">
				<OrganizationWorkspaceLauncher :interactive="!emptyWorkspaceShell" />
			</div>
			<Spacebar
				v-if="workspaceRuntime.activeRuntime.value !== null"
				:key="workspaceRuntimeKey"
				:workspace-switching="emptyWorkspaceShell"
				:aria-label="$t('common.main_navigation')"
				class="transition-colors"
			>
				<template #user>
					<UserSettingsButton />
				</template>
			</Spacebar>
		</aside>
		<main
			class="flex flex-1 overflow-hidden"
			:class="emptyWorkspaceShell ? 'flex-row' : 'flex-col'"
			:data-testid="emptyWorkspaceShell ? 'workspace-switching-shell' : undefined"
		>
			<template v-if="emptyWorkspaceShell">
				<div
					v-if="paneStore.sidebarUseSplitPanel && !paneStore.sidebarSplitPanelCollapsed"
					class="shrink-0"
					:style="{ inlineSize: `${paneStore.sidebarSize}px` }"
				/>

				<div class="flex min-w-0 flex-1 flex-col pe-2 pbe-2">
					<div class="shrink-0 block-(--primary-header-height)" />
					<div
						class="flex min-h-0 flex-1 items-center justify-center rounded-sm border bg-default"
						:role="showWorkspaceSwitching ? 'status' : undefined"
						:aria-label="showWorkspaceSwitching ? $t('common.loading') : undefined"
					>
						<DSpinner v-if="showWorkspaceSwitching" />
					</div>
				</div>
			</template>

			<div
				v-if="renderWorkspaceContent"
				v-show="!emptyWorkspaceShell"
				:key="workspaceRuntimeKey"
				:inert="emptyWorkspaceShell"
				class="contents"
			>
				<ErrorBoundary>
					<slot />
				</ErrorBoundary>
			</div>
		</main>
		<SettingsOverlayModal />
	</div>
</template>

<style>
/* Prevent overscroll on MacOS */
html, body {
	overflow-x: hidden;
	overflow-y: hidden;
}
</style>
